import React, { useState, useRef, useEffect } from "react";
import styles from "styles/genai/Edetail.module.scss";
import zoominus from "img/genai/minus.svg";
import zoomplus from "img/genai/plus.svg";
import rightpager from "img/genai/rightpager.svg";
import SlidePagination from "../utils/SlidePagination";

export const PreviewModal = ({ show, onClose, previewData }) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [imageSrc, setImageSrc] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isEditingZoom, setIsEditingZoom] = useState(false);
  const [tempZoom, setTempZoom] = useState(zoomLevel);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const zoomInputRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const [containerRect, setContainerRect] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const totalSlides = previewData?.pageDetails?.length || 0;
  const currentPage = previewData?.pageDetails?.[currentSlide - 1] ?? null;

  const preventContextMenu = (e) => e.preventDefault();

  useEffect(() => {
    if (show && containerRef.current) {
      const updateContainerRect = () => {
        setContainerRect(containerRef.current.getBoundingClientRect());
      };

      updateContainerRect();
      window.addEventListener("resize", updateContainerRect);
      return () => window.removeEventListener("resize", updateContainerRect);
    }
  }, [show, currentSlide]);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + 10, 200);
    setZoomLevel(newZoom);
    setTempZoom(newZoom);
    centerImage();
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - 10, 50);
    setZoomLevel(newZoom);
    setTempZoom(newZoom);
    centerImage();
  };

  const centerImage = () => {
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setTempZoom(value);
  };

  const handleZoomBlur = () => {
    const validatedZoom = Math.min(Math.max(tempZoom, 50), 300);
    setZoomLevel(validatedZoom);
    setTempZoom(validatedZoom);
    setIsEditingZoom(false);
    centerImage();
  };

  const handleZoomKeyDown = (e) => {
    if (e.key === "Enter") handleZoomBlur();
  };

  const handleZoomClick = () => setIsEditingZoom(true);

  useEffect(() => {
    if (show) {
      setCurrentSlide(1);
      setZoomLevel(100);
      setImageSrc("");
      centerImage();
    }
  }, [show, previewData]);

  useEffect(() => {
    if (!currentPage?.signedUrl) return;

    setIsImageLoading(true);
    const img = new Image();
    img.src = currentPage.signedUrl;

    img.onload = () => {
      setImageSrc(currentPage.signedUrl);
      setIsImageLoading(false);
      centerImage();
    };

    img.onerror = () => {
      console.warn("Failed to load image", currentPage.signedUrl);
      setIsImageLoading(false);
    };
  }, [currentPage]);

  const handleSlideChange = (newSlide) => {
    if (newSlide < 1 || newSlide > totalSlides) return;
    setCurrentSlide(newSlide);
    setZoomLevel(100);
    setImageSrc("");
    centerImage();
  };

  // Panning handlers
  const handleMouseDown = (e) => {
    if (zoomLevel <= 100) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 100) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Calculate max position based on zoom level
    const maxX = (zoomLevel - 100) * (containerRect?.width || 0) / 200;
    const maxY = (zoomLevel - 100) * (containerRect?.height || 0) / 200;
    
    setPosition({
      x: Math.min(Math.max(newX, -maxX), maxX),
      y: Math.min(Math.max(newY, -maxY), maxY)
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (zoomLevel <= 100) return;
    
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || zoomLevel <= 100) return;
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    // Calculate max position based on zoom level
    const maxX = (zoomLevel - 100) * (containerRect?.width || 0) / 200;
    const maxY = (zoomLevel - 100) * (containerRect?.height || 0) / 200;
    
    setPosition({
      x: Math.min(Math.max(newX, -maxX), maxX),
      y: Math.min(Math.max(newY, -maxY), maxY)
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose} onContextMenu={preventContextMenu}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header with zoom controls */}
        <article className={styles.modalHead}>
          <div className={styles.previewLeft}>
            <strong>Preview</strong>
          </div>
          <div className={styles.zoompreview}>
            <span
              onClick={zoomLevel > 50 ? handleZoomOut : undefined}
              className={`${styles.zoomControl} ${
                zoomLevel <= 50 ? styles.disabledControl : ""
              }`}
              role="button"
              tabIndex="0"
              aria-label="Zoom out"
              aria-disabled={zoomLevel <= 50}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && zoomLevel > 50 && handleZoomOut()}
            >
              <img src={zoominus} alt="Zoom out" />
            </span>
  
            {isEditingZoom ? (
              <input
                ref={zoomInputRef}
                type="number"
                value={tempZoom}
                onChange={handleZoomChange}
                onBlur={handleZoomBlur}
                onKeyDown={handleZoomKeyDown}
                min="50"
                max="300"
                className={styles.zoomInput}
                aria-label="Zoom percentage"
              />
            ) : (
              <span
                onClick={handleZoomClick}
                className={styles.zoomDisplay}
                role="button"
                tabIndex="0"
                aria-label="Edit zoom"
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleZoomClick()}
              >
                {zoomLevel}%
              </span>
            )}
  
            <span
              onClick={zoomLevel < 300 ? handleZoomIn : undefined}
              className={`${styles.zoomControl} ${
                zoomLevel >= 300 ? styles.disabledControl : ""
              }`}
              role="button"
              tabIndex="0"
              aria-label="Zoom in"
              aria-disabled={zoomLevel >= 300}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && zoomLevel < 300 && handleZoomIn()}
            >
              <img src={zoomplus} alt="Zoom in" />
            </span>
  
          </div>
        </article>
  
        {/* Image container with panning */}
        <div
          ref={containerRef}
          className={styles.modalContainerImg}
          style={{
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: zoomLevel > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            touchAction: 'none'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isImageLoading ? (
            <div className={styles.imageLoading}>Loading...</div>
          ) : (
            imageSrc && (
              <img
                ref={imageRef}
                src={imageSrc}
                alt={`Page ${currentPage?.pageNo || ""}`}
                style={{
                  transform: `scale(${zoomLevel / 100}) translate(${position.x}px, ${position.y}px)`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                  willChange: "transform",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  userSelect: 'none'
                }}
                onDragStart={(e) => e.preventDefault()}
              />
            )
          )}
        </div>
  
        {/* Footer with pagination */}
        <article className={styles.modalBottom}>
          <div></div>
          {totalSlides > 1 && (
            <SlidePagination
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              onSlideChange={handleSlideChange}
            />
          )}
          <div>
            <button
              onClick={onClose}
              className={styles.closeModalBtn}
              role="button"
              tabIndex="0"
              aria-label="Close preview"
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClose()}
            >
              CLOSE
            </button>
          </div>
        </article>
      </div>
    </div>
  );
};

